import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Exact, type InvoiceDto, type InvoiceKind, type InvoicePrintHeaderDto, type InvoicePrintsDto, type PrintLayout, type PrintRecordDto } from "@delta/shared";
import { useLang, type T } from "../../i18n";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/Toast";
import { amt, instant, when } from "../ledger/common";
import { Barcode, dec, qty as fmtQty } from "../items/common";
import { claimBrowserPrint } from "../../print/direct";
import { carriesQr, Phase1Qr } from "../../print/qr";
import "../../styles/invoice-print.css";
import { partyName } from "../../lib/party";
import { DemoPrintMark } from "../../components/Demo";

// /invoices/print/:id?paper=a4|80|58: the printed invoice, a page of its own. It prints the document, never the
// screen: no menu, buttons, rule notes, e-invoice panels or empty sections; the seller, the client or supplier, the
// number and date, every line's columns and the totals. A4 repeats the column heads on each page and keeps a line,
// the totals and the notice whole; the receipts are one page as long as their content.
//
// While ZATCA phase 2 is deferred, a sale or sales refund prints as a non-fiscal statement: its title and a boxed
// notice say it is not a tax invoice. A posted one carries the phase 1 QR (print/qr.tsx: the five TLV fields only,
// not phase 2, not validated with ZATCA), or a box saying the seller's tax details are missing or invalid. A server
// that is not production (staging, development, tests) marks every page TEST DOCUMENT. Times are Saudi time.

type Paper = "a4" | "80" | "58";
const LAYOUT: Record<Paper, PrintLayout> = { a4: "a4", "80": "receipt_80", "58": "receipt_58" };
const TITLE: Record<InvoiceKind, string> = {
  sale: "printSaleTitle", sale_refund: "printSaleRefundTitle", purchase: "purchaseInvoice", purchase_refund: "purchaseRefundTitle", quotation: "quotationTitle"
};
const D = Exact;
const nz = (x: string | null | undefined) => !!x && /[1-9]/.test(x);
const buys = (k: InvoiceKind) => k === "purchase" || k === "purchase_refund";
const fiscalSide = (k: InvoiceKind) => k === "sale" || k === "sale_refund";
/** A name or free text in either language, isolated so its punctuation stays with it ("Co." in Arabic). */
const B = ({ children }: { children: ReactNode }) => <bdi>{children}</bdi>;
/** A number or code kept left to right inside Arabic text. */
const N = ({ children, b }: { children: ReactNode; b?: boolean }) => <bdi dir="ltr" className={b ? "num b" : "num"}>{children}</bdi>;
const lineDiscount = (l: InvoiceDto["lines"][number]) => D.fmt(D.add(D.dec(l.lineDiscount), D.dec(l.invoiceDiscount)), 2);
const rate = (r: string) => `${r.replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "")}%`;

interface Props { d: InvoiceDto; h: InvoicePrintHeaderDto; copyNo: number; printedAt: string }

function Banners({ d, h, copyNo, t }: Props & { t: T }) {
  return <>
    {h.testDocument && <div className="ip-test" data-testid="ip-test">{t("printTestBanner")}</div>}
    <DemoPrintMark className="ip-test ip-demo" />
    {d.status === "draft" && d.kind !== "quotation" && <div className="ip-banner" data-testid="ip-draft">{t("printDraftBanner")}</div>}
    {d.reversedAt && <div className="ip-banner" data-testid="ip-reversed">{t("printReversedBanner", { d: when(d.reversalAt ?? d.reversedAt.slice(0, 16)) })}</div>}
    {copyNo > 0 && <div className="ip-banner" data-testid="ip-copy">{t("printCopyBanner", { n: copyNo })}</div>}
  </>;
}

function nonFiscal(t: T, h: InvoicePrintHeaderDto) {
  return h.seller.legalInvoiceSystem ? t("printNonFiscalSystem", { system: h.seller.legalInvoiceSystem }) : t("printNonFiscal");
}

function A4({ d, h, copyNo, printedAt }: Props) {
  const { t, L, lang } = useLang();
  const s = h.seller, p = h.party, quote = d.kind === "quotation", refund = d.kind === "sale_refund" || d.kind === "purchase_refund";
  const name = lang === "ar" ? s.nameAr || s.nameEn : s.nameEn || s.nameAr, other = lang === "ar" ? s.nameEn : s.nameAr;
  const meta: [string, ReactNode][] = [
    [t(quote ? "quotationNo" : "printNumber"), <N b>{d.code ?? t("journalDraft")}</N>],
    [t("date"), <N>{when(d.invoiceAt)}</N>]
  ];
  if (d.refundOf) meta.push([t("refundOfLabel"), <N>{d.refundOf.code}</N>]);
  if (d.quotation) meta.push([t("fromQuotation"), <N>{d.quotation.code}</N>]);
  meta.push([t("branch"), <B>{L(d.branch)}</B>], [t("warehouse"), <B>{L(d.warehouse)}</B>]);
  if (d.reference) meta.push([t("paidReference"), <N>{d.reference}</N>]);
  if (d.legalReference) meta.push([t("legalInvoiceNo"), <N b>{d.legalReference}</N>]);
  if (d.legacyNumber) meta.push([t("legacyNumber"), <N>{d.legacyNumber}</N>]);
  const totals: [string, string, string?][] = [];
  if (nz(d.discount)) totals.push([t("totalBeforeDiscount"), amt(d.totalBefore)], [t("discount"), `-${amt(d.discount)}`]);
  totals.push([t("subTotal"), amt(d.subtotal)]);
  // Tobacco municipality fees (migration 049): shown apart from VAT.
  if (nz(d.tobaccoFee)) totals.push([t("tobFee"), amt(d.tobaccoFee)]);
  totals.push([`${t("tax")} (${t("vat")})`, amt(d.vat)], [t("grandTotal"), amt(d.grand), "g"]);
  if (!quote) {
    if (nz(d.paid)) totals.push([t(refund ? "refundedAmount" : "paid"), amt(d.paid)]);
    if (nz(d.settled)) totals.push([t("settledAmount"), amt(d.settled)]);
    if (!refund && nz(d.remaining) && !d.remaining.startsWith("-")) totals.push([t("remaining"), amt(d.remaining), "due"]);
  }
  return (
    <div className="ip-doc ip-a4" data-testid="ip-doc" data-paper="a4">
      {h.testDocument && <div className="ip-mark" aria-hidden="true">{t("printTestMark")}</div>}
      <Banners d={d} h={h} copyNo={copyNo} printedAt={printedAt} t={t} />
      <header className="ip-head">
        <div className="ip-seller" data-testid="ip-seller">
          {s.hasLogo && <img className="ip-logo" src="/api/organization/logo" alt="" />}
          <div>
            <div className="ip-org"><B>{name}</B></div>
            {other && other !== name && <div className="ip-org2" lang={lang === "ar" ? "en" : "ar"}><B>{other}</B></div>}
            {s.taxNumber && <div>{t("vatNo")}: <N>{s.taxNumber}</N></div>}
            {s.commercialRecord && <div>{t("commercialRecord")}: <N>{s.commercialRecord}</N></div>}
            {s.address.map((a) => <div key={a}><B>{a}</B></div>)}
            {(s.phone || s.email) && <div>{s.phone && <N>{s.phone}</N>}{s.phone && s.email && " · "}{s.email && <N>{s.email}</N>}</div>}
          </div>
        </div>
        <div className="ip-docbox">
          <h1 className="ip-title" data-testid="ip-title">{t(TITLE[d.kind])}</h1>
          <dl className="ip-meta">{meta.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
        </div>
      </header>
      <section className="ip-party" data-testid="ip-party">
        <div className="ip-label">{t(buys(d.kind) ? "printSupplier" : "printCustomer")}</div>
        <div className="ip-pname" data-testid="ip-pname"><B>{d.party ? p.name : partyName(t, null)}</B></div>
        <div className="ip-pdet">
          {p.taxNumber && <span>{t("vatNo")}: <N>{p.taxNumber}</N></span>}
          {p.commercialRecord && <span>{t("commercialRecord")}: <N>{p.commercialRecord}</N></span>}
          {p.phone && <span>{t("phone")}: <N>{p.phone}</N></span>}
          {p.address && <span><B>{p.address}</B></span>}
        </div>
      </section>
      <table className="ip-tbl" data-testid="ip-lines">
        <colgroup><col className="c-no" /><col className="c-item" /><col className="c-unit" /><col className="c-qty" /><col className="c-price" /><col className="c-disc" />
          <col className="c-amt" /><col className="c-rate" /><col className="c-vat" /><col className="c-amt" /></colgroup>
        <thead><tr><th>#</th><th>{t("itemName")}</th><th>{t("unitName")}</th><th className="e">{t("qty")}</th><th className="e">{t("price")}</th><th className="e">{t("discount")}</th>
          <th className="e">{t("beforeTax")}</th><th className="e">{t("printVatRate")}</th><th className="e">{t("taxAmount")}</th><th className="e">{t("lineTotal")}</th></tr></thead>
        <tbody>{d.lines.map((l) => (
          <tr key={l.lineNo}>
            <td className="e"><N>{l.lineNo}</N></td>
            <td><div className="ip-iname"><B>{L(l.item)}</B></div>{l.item.code && <div className="ip-sub"><N>{l.item.code}</N></div>}</td>
            <td><B>{L(l.unit)}</B></td>
            <td className="e"><N>{fmtQty(l.qty)}</N></td>
            <td className="e"><N>{dec(l.price, 2)}</N>{l.priceIncludesTax && <div className="ip-sub">{t("inclVat")}</div>}</td>
            <td className="e">{nz(lineDiscount(l)) ? <N>{amt(lineDiscount(l))}</N> : "–"}</td>
            <td className="e"><N>{amt(l.taxable)}</N>{nz(l.tobaccoFee) && <div className="ip-sub" data-testid={`ip-fee-${l.lineNo}`}>{t("tobFeeShort", { x: amt(l.tobaccoFee) })}</div>}</td>
            <td className="e"><N>{rate(l.taxType.rate)}</N></td>
            <td className="e"><N>{amt(l.tax)}</N></td>
            <td className="e"><N b>{amt(l.total)}</N></td>
          </tr>
        ))}</tbody>
      </table>
      <div className="ip-foot">
        <div className="ip-notes">
          {!quote && d.payments.length > 0 && <div className="ip-pays" data-testid="ip-payments"><div className="ip-label">{t("payments")}</div>
            {d.payments.map((x) => <div key={x.treasury.id} className="ip-kv"><span><B>{L(x.treasury)}</B></span><N>{amt(x.amount)}</N></div>)}</div>}
          {d.note && <div className="ip-note" data-testid="ip-note"><div className="ip-label">{t("note")}</div><div dir="auto">{d.note}</div></div>}
        </div>
        <div className="ip-totals" data-testid="ip-totals">{totals.map(([k, v, c]) => <div key={k} className={c ? `ip-kv ${c}` : "ip-kv"}><span>{k}</span><span><N b={c === "g"}>{v}</N>{c === "g" && <> {t("currency")}</>}</span></div>)}
          {!refund && nz(d.tobacco?.raised ?? "0") && <div className="ip-sub" data-testid="ip-tobacco-raised">{t("tobRaisedNote", { x: amt(d.tobacco!.raised) })}</div>}</div>
      </div>
      {fiscalSide(d.kind) && <div className="ip-fiscal">
        {carriesQr(d) && <Phase1Qr seller={s} inv={d} t={t} mm={30} />}
        <div className="ip-nonfiscal" data-testid="ip-nonfiscal">{nonFiscal(t, h)}</div>
      </div>}
      <footer className="ip-end"><span>{t("printedAt", { d: printedAt })}</span><span>{t("printTimeZone")}</span></footer>
    </div>
  );
}

function Receipt({ d, h, copyNo, printedAt, paper }: Props & { paper: "80" | "58" }) {
  const { t, L, lang } = useLang();
  const s = h.seller, quote = d.kind === "quotation", refund = d.kind === "sale_refund" || d.kind === "purchase_refund";
  const name = lang === "ar" ? s.nameAr || s.nameEn : s.nameEn || s.nameAr;
  const kv = (k: string, v: ReactNode, c?: string) => <div className={c ? `ip-kv ${c}` : "ip-kv"} key={k}><span>{k}</span>{v}</div>;
  return (
    <div className={`ip-doc ip-rc ip-rc${paper}`} data-testid="ip-doc" data-paper={paper}>
      <Banners d={d} h={h} copyNo={copyNo} printedAt={printedAt} t={t} />
      <div className="ip-rtitle" data-testid="ip-title">{fiscalSide(d.kind) ? t(refund ? "nonFiscalRefundTitle" : "nonFiscalTitle") : t(TITLE[d.kind])}</div>
      <div className="ip-center">
        {s.hasLogo && <img className="ip-rlogo" src="/api/organization/logo" alt="" data-testid="ip-rlogo" />}
        <div className="ip-org"><B>{name}</B></div>
        {s.taxNumber && <div>{t("vatNo")}: <N>{s.taxNumber}</N></div>}
        {s.commercialRecord && <div>{t("commercialRecord")}: <N>{s.commercialRecord}</N></div>}
        {s.address.length > 0 && <div><B>{s.address.join(", ")}</B></div>}
        {s.phone && <div><N>{s.phone}</N></div>}
      </div>
      <hr />
      {kv(t(quote ? "quotationNo" : "printNumber"), <N b>{d.code ?? t("journalDraft")}</N>)}
      {d.refundOf && kv(t("refundOfLabel"), <N>{d.refundOf.code}</N>)}
      {kv(t("date"), <N>{when(d.invoiceAt)}</N>)}
      {kv(t(buys(d.kind) ? "printSupplier" : "printCustomer"), <span className="ip-v"><B>{partyName(t, d.party)}</B></span>)}
      {h.party.taxNumber && kv(t("vatNo"), <N>{h.party.taxNumber}</N>)}
      {kv(t("branch"), <span className="ip-v"><B>{L(d.branch)}</B></span>)}
      {d.shift && kv(t("shiftNo"), <N>{d.shift.number}</N>)}
      <hr className="dash" />
      <div className="ip-rlines" data-testid="ip-lines">{d.lines.map((l) => (
        <div key={l.lineNo} className="ip-rline">
          <div className="ip-iname"><B>{L(l.item)}</B> <span className="ip-sub">(<B>{L(l.unit)}</B>)</span></div>
          <div className="ip-kv"><span><N>{fmtQty(l.qty)}</N> × <N>{dec(l.price, 2)}</N>{nz(lineDiscount(l)) && <> − <N>{amt(lineDiscount(l))}</N></>}</span><N b>{amt(l.total)}</N></div>
          {nz(l.tobaccoFee) && <div className="ip-kv ip-sub"><span>{t("tobFee")}</span><N>{amt(l.tobaccoFee)}</N></div>}
          <div className="ip-kv ip-sub"><span>{t("tax")} <N>{rate(l.taxType.rate)}</N></span><N>{amt(l.tax)}</N></div>
        </div>
      ))}</div>
      <hr className="dash" />
      {nz(d.discount) && kv(t("totalBeforeDiscount"), <N>{amt(d.totalBefore)}</N>)}
      {nz(d.discount) && kv(t("discount"), <N>-{amt(d.discount)}</N>)}
      {kv(t("subTotal"), <N>{amt(d.subtotal)}</N>)}
      {nz(d.tobaccoFee) && kv(t("tobFee"), <N>{amt(d.tobaccoFee)}</N>)}
      {kv(`${t("tax")} (${t("vat")})`, <N>{amt(d.vat)}</N>)}
      {kv(t("grandTotal"), <span><N b>{amt(d.grand)}</N> {t("currency")}</span>, "g")}
      {!refund && nz(d.tobacco?.raised ?? "0") && <div className="ip-sub">{t("tobRaisedNote", { x: amt(d.tobacco!.raised) })}</div>}
      {!quote && d.payments.map((x) => kv(L(x.treasury), <N>{amt(x.amount)}</N>))}
      {!quote && !refund && nz(d.remaining) && !d.remaining.startsWith("-") && kv(t("remaining"), <N>{amt(d.remaining)}</N>)}
      {fiscalSide(d.kind) && <div className="ip-nonfiscal" data-testid="ip-nonfiscal">{nonFiscal(t, h)}</div>}
      {d.legalReference && kv(t("legalInvoiceNo"), <N b>{d.legalReference}</N>)}
      {carriesQr(d) && <Phase1Qr seller={s} inv={d} t={t} mm={paper === "80" ? 32 : 28} className="ip-rqr" />}
      {d.note && <div className="ip-note" data-testid="ip-note" dir="auto">{d.note}</div>}
      {d.code && <div className="ip-center ip-code"><Barcode text={d.code} h={32} /></div>}
      <div className="ip-center ip-sub">{t("printedAt", { d: printedAt })} · UTC+3</div>
    </div>
  );
}

export function InvoicePrint() {
  const { id } = useParams();
  const [qs, setQs] = useSearchParams();
  const { t, L } = useLang();
  const toast = useToast();
  const paper: Paper = qs.get("paper") === "80" ? "80" : qs.get("paper") === "58" ? "58" : "a4";
  const [d, setD] = useState<InvoiceDto | null>(null);
  const [h, setH] = useState<InvoicePrintHeaderDto | null>(null);
  const [prints, setPrints] = useState<InvoicePrintsDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<number | null>(null);
  const [printedAt, setPrintedAt] = useState(() => instant(new Date().toISOString()));
  const [height, setHeight] = useState<number | null>(null);
  const sheet = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setD(null); setFailed(null);
    Promise.all([api<InvoiceDto>("GET", `/invoices/${id}`), api<InvoicePrintHeaderDto>("GET", `/invoices/${id}/print-header`), api<InvoicePrintsDto>("GET", `/invoices/${id}/prints`)])
      .then(([x, y, z]) => { setD(x); setH(y); setPrints(z); }, (e) => setFailed(errorText(t, e)));
  }, [id, t]);
  useEffect(() => {
    document.body.classList.add("ip-open");
    return () => document.body.classList.remove("ip-open");
  }, []);
  // A receipt is one page as long as its content: measure it once laid out (96 px to the inch).
  useLayoutEffect(() => {
    const el = sheet.current?.querySelector<HTMLElement>(".ip-doc");
    setHeight(paper === "a4" || !el ? null : Math.ceil((el.getBoundingClientRect().height * 25.4) / 96) + 2);
  }, [paper, d, h, claimed, printedAt]);
  useEffect(() => { if (d) document.title = `${d.code ?? t("journalDraft")} · ${t(TITLE[d.kind])}`; }, [d, t]);

  if (failed) return <div className="state-box" role="alert"><p>{failed}</p></div>;
  if (!d || !h || !prints) return <div className="state-box"><p>{t("loading")}</p></div>;
  // A completed or posted document (or a quotation) claims its original once and numbers every later print as a copy.
  const recorded = d.status !== "draft" || d.kind === "quotation";
  const original = prints.prints.some((p) => p.copyNo === 0);
  const nextCopy = original ? Math.max(...prints.prints.map((p) => p.copyNo)) + 1 : 0;
  const copyNo = claimed ?? (recorded ? nextCopy : 0);
  const doPrint = async () => {
    try {
      if (recorded) {
        const r = await claimBrowserPrint(d.id, LAYOUT[paper], original);
        const rec: PrintRecordDto = "originalExists" in r ? await claimBrowserPrint(d.id, LAYOUT[paper], true) as PrintRecordDto : r;
        setClaimed(rec.copyNo);
        setPrints(await api<InvoicePrintsDto>("GET", `/invoices/${d.id}/prints`));
      }
      setPrintedAt(instant(new Date().toISOString()));
      setTimeout(() => window.print(), 80);
    } catch (e) { toast(errorText(t, e)); }
  };
  const page = paper === "a4"
    ? "@page { size: A4; margin: 12mm 12mm 14mm; @bottom-center { content: \"\\2066\" counter(page) \" / \" counter(pages) \"\\2069\"; font: 8pt sans-serif; color: #555; } }"
    : `@page { size: ${paper}mm ${height ?? 200}mm; margin: 0; }`;
  const props = { d, h, copyNo, printedAt };
  return (
    <div className="ip-root">
      <style>{page}</style>
      <div className="ip-bar" data-testid="ip-bar">
        <Link className="btn" to={`/invoices/show/${d.id}`}><Icon name="back" />{t("printBack")}</Link>
        <b className="ip-bar-t">{t(TITLE[d.kind])} <span className="num">{d.code ?? ""}</span> · {L(d.branch)}</b>
        <div className="segmented" role="radiogroup" aria-label={t("printPaper")}>{(["a4", "80", "58"] as const).map((v) => (
          <label key={v}><input type="radio" name="ip-paper" value={v} checked={paper === v} onChange={() => { setClaimed(null); setQs({ paper: v }, { replace: true }); }} data-testid={`ip-paper-${v}`} />
            <span>{t(v === "a4" ? "printPaperA4" : v === "80" ? "printPaper80" : "printPaper58")}</span></label>))}</div>
        <button className="btn btn-primary" onClick={() => void doPrint()} data-testid="ip-print"><Icon name="printer" />{copyNo > 0 ? t("printCopy") : t("printDocument")}</button>
      </div>
      <div className={`ip-stage ip-stage-${paper}`} ref={sheet}>{paper === "a4" ? <A4 {...props} /> : <Receipt {...props} paper={paper} />}</div>
    </div>
  );
}
